// The single requestAnimationFrame loop. Reads the active playback source's time, moves the
// alphaTab cursor (song mode), and pushes only the current-chord label to React (throttled by
// change). Ported from poc/web/app.js syncLoop. The cursor is written imperatively — never via
// per-frame React state.
import type { Controllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { useEditorStore } from "../state/editorStore";
import { tickForTime, timeForTick } from "../core/timeMap";

export class CursorLoop {
  private started = false;
  private raf = 0;
  private lastChord = "";
  private lastPlaying = false;
  private lastMeasure = -1;
  private lastBeatTime = -1;

  constructor(private c: Controllers) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    const tick = () => {
      this.frame();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.started = false;
    cancelAnimationFrame(this.raf);
  }

  private frame(): void {
    const proj = useProjectStore.getState();
    const pb = usePlaybackStore.getState();
    const analysis = proj.analysis;
    const measures = proj.chartMeasures;
    if (!analysis) return;

    const src = this.c.activeSource();
    const scorePlaying = this.c.score.playing;
    const songPlaying = src.isPlaying();
    const t = src.getPlaybackTime();

    // display time: score mode derives from the synth cursor; song mode uses the song clock
    let dt = t;
    if (pb.playMode === "score" && scorePlaying) {
      try { dt = timeForTick(measures, this.c.score.rawApi?.tickPosition ?? 0); } catch { /* noop */ }
    }

    // current chord label (push to React only when it changes)
    const segs = analysis.chords || [];
    let chord = "—";
    for (let i = 0; i < segs.length; i++) {
      if (dt >= segs[i].start && dt < segs[i].end) { chord = segs[i].chord; break; }
    }
    if (chord !== this.lastChord) { this.lastChord = chord; pb.setCurrentChord(chord); }

    // current measure (for the chord-grid view) — push to React only when it changes
    let measure = -1;
    for (let i = 0; i < measures.length; i++) { if (dt >= measures[i].startTime - 0.02) measure = i; else break; }
    if (measure !== this.lastMeasure) { this.lastMeasure = measure; pb.setCurrentMeasure(measure); }

    // start time of the beat under the cursor — lets the grid highlight the exact block
    // (so a measure with the same chord twice, e.g. "G Gmaj7 G", doesn't light both G's)
    let beatTime = -1;
    const cm = measures[measure];
    if (cm) for (const b of cm.beats) { if (dt >= b.time - 0.02) beatTime = b.time; else break; }
    if (beatTime !== this.lastBeatTime) { this.lastBeatTime = beatTime; pb.setCurrentBeatTime(beatTime); }

    // song mode (file source only): stop cleanly at the end of the buffer
    if (pb.playMode === "song" && songPlaying && src === this.c.song) {
      const dur = this.c.song.duration;
      if (dur && t >= dur - 0.05) { this.c.song.pause(); this.c.song.setBaseTime(0); }
    }

    // transport state must reflect the ACTIVE source (file or YouTube) — so playing/pausing the
    // YouTube video via its own controls keeps the play button + cursor in sync.
    const playing = pb.playMode === "song" ? songPlaying : scorePlaying;
    if (playing !== this.lastPlaying) { this.lastPlaying = playing; pb.setIsPlaying(playing); }
    // edit mode: cursor only while playing (so selection coloring reads cleanly). Otherwise always
    // show it, so you can see the playback position even when paused.
    this.c.score.setCursorVisible(playing || !useEditorStore.getState().editMode);

    if (scorePlaying) this.c.score.resumeAudio();
    // song mode + score rendered: move the cursor to the song position. (Score mode: alphaTab's
    // own player drives both sound and cursor, so we never touch tickPosition while it plays.)
    if (pb.playMode === "song" && songPlaying && this.c.score.ready && measures.length) {
      this.c.score.setTickPosition(tickForTime(measures, t));
    }
  }
}
